import type { CancellationFeeType } from "@prisma/client";

// Shared between the client-facing PaymentStep display and the server-side
// snapshot taken inside the booking-creation transaction — keeps both
// computations identical without duplicating the PER_PERSON/FLAT branch.
export const computeCancellationFeeAmountCents = (
    feeType: CancellationFeeType,
    baseAmountCents: number,
    guestCount: number,
): number => (feeType === "PER_PERSON" ? baseAmountCents * guestCount : baseAmountCents);

// Stripe's minimum charge for AUD. Anything lower is rejected by the PaymentIntent API, so the
// admin sees a validation message instead of a card-decline-looking Stripe error. To charge
// nothing, the admin waives the fee instead.
export const MIN_CANCELLATION_FEE_CHARGE_CENTS = 50;

const formatDollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

// Shared by the charge server action (authoritative) and the charge dialog (early feedback).
// The upper bound is the fee's policy snapshot: that's the amount the guest agreed to and was
// told about in the confirmation email, so an admin may reduce it but never exceed it.
export const validateChargeAmountCents = (amountCents: number, maxCents: number): string | null => {
    if (!Number.isInteger(amountCents)) return "Enter a valid amount";
    if (amountCents < MIN_CANCELLATION_FEE_CHARGE_CENTS) {
        return `Minimum is ${formatDollars(MIN_CANCELLATION_FEE_CHARGE_CENTS)} — waive the fee instead to charge nothing`;
    }
    if (amountCents > maxCents) {
        return `Cannot exceed the policy amount of ${formatDollars(maxCents)}`;
    }
    return null;
};

// Parses the dialog's dollar text ("35", "35.5", "35.50") into whole cents, or null when the text
// isn't a plain dollars-and-cents amount.
export const parseDollarsToCents = (input: string): number | null => {
    const trimmed = input.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
    return Math.round(Number(trimmed) * 100);
};
